import type { LabelType } from './LabelsContext';

/**
 * Cache global pour les Maps de labels
 * Persiste entre les remontages React Strict Mode
 *
 * Clé: `"${locale}:${labelType}"`
 * Valeur: { hash, map }
 *
 * CRITICAL: Ce cache garantit que les même Maps sont réutilisées
 * même quand les props changent de référence mais ont le même contenu
 */
export const globalLabelsCache = new Map<
  string,
  { hash: string; map: Map<string, string> }
>();

/**
 * Génère un hash du contenu d'un objet Record<string, string>
 * Pour détecter si le contenu a changé (vs juste la référence)
 */
export function hashRecord(record: Record<string, string>): string {
  const entries = Object.entries(record).sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k, v]) => `${k}:${v}`).join('|');
}

/**
 * Crée ou réutilise une Map depuis le cache global
 * Garantit la stabilité de la référence Map entre les remontages
 *
 * La clé est préfixée par locale pour supporter l'i18n
 */
export function getOrCreateMap(
  locale: string,
  labelType: LabelType,
  record: Record<string, string>,
): Map<string, string> {
  const hash = hashRecord(record);
  const cacheKey = `${locale}:${labelType}`;
  const cached = globalLabelsCache.get(cacheKey);

  if (cached && cached.hash === hash) {
    return cached.map;
  }

  const newMap = new Map(Object.entries(record));
  globalLabelsCache.set(cacheKey, { hash, map: newMap });
  return newMap;
}
